/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Input, Checkbox, Table, Switch, Tag as TagAntd, message } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TableProps } from 'antd/lib/table';
import { useMemo, CSSProperties, useState } from 'react';
import { useIntl } from 'react-intl';
import IconButton from '~components/_common/IconButton/IconButton';
import { CreateEditTagModal } from '~components/popups-modals/CreateEditTag.modal';
import { DeleteTagModal } from '~components/popups-modals/DeleteTag.modal';
import { RestoreTagModal } from '~components/popups-modals/RestoreTag.modal';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { fadeInKeyframes } from '~src/styles/animations';

import { DB_STATE } from '~src/types/enums';
import { tabStyleFn } from './tabs.util';
import { useArchiveSettings } from '~src/hooks/api-hooks/useArchiveSettings';
import { Tag, updateArchiveSettings } from '~src/services/api/archiveSettings';
import { appLog } from '~src/utils/log';
import { IconPopup } from '~components/popups-modals/DetailsLayouts/components';
import { useMutation } from 'react-query';
import { saveUserSettings } from '~src/services/api/user';
import { useUserSettings } from '~src/hooks/api-hooks/useUserSettings';
import { useProjectId } from '~src/hooks/useProjectId';
import { queryCache } from 'react-query';

export interface TagsTabProps {
  loading?: boolean;
}

export const TagsTab: React.FC<TagsTabProps> = ({ loading }) => {
  const intl = useIntl();
  const style = useStyles(styleFn);
  const tabStyles = useStyles(tabStyleFn);

  const archiveSettings = useArchiveSettings();

  const tableData = archiveSettings.data?.resTags;

  const [createEditModalVisible, setCreateEditModalVisible] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [restoreeModal, setRestoreModal] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedTag, setSelectedTag] = useState<Tag | null>(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const userSettings = useUserSettings();
  const projectId = useProjectId();
  const [isSwitchLoading, setIsSwitchLoading] = useState(false);

  const filteredTableData = useMemo(() => {
    if (!searchValue && showDeleted) return tableData;
    return tableData?.filter(
      (tag) =>
        tag.title.toLowerCase().includes(searchValue.toLowerCase()) &&
        (showDeleted || Number(tag.state) !== DB_STATE.REMOVED)
    );
  }, [tableData, searchValue, showDeleted]);

  const dataSourceWithKeys = filteredTableData?.map((item, index) => ({
    ...item,
    key: item.id || index,
  }));

  const [doSaveUserSettings] = useMutation(saveUserSettings, {
    onError: (err) => {
      appLog('err -> ', err);
      message.error(intl.formatMessage({ id: 'notification-toggle-error' }));
    },
    onSuccess: (data, variables) => {
      message.success(intl.formatMessage({ id: 'changes-saved-successfully' }));
    },
  });

  const [doSaveArchiveSettings] = useMutation(updateArchiveSettings, {
    onError: (err) => {
      appLog('err -> ', err);
      message.error(intl.formatMessage({ id: 'notification-toggle-error' }));
      setIsSwitchLoading(false);
    },
    onSuccess: (data, variables) => {
      message.success(intl.formatMessage({ id: 'changes-saved-successfully' }));
      queryCache.invalidateQueries('archiveSettingsList');
      setIsSwitchLoading(false);
    },
  });

  const getColorRow = (record) =>
    Number(record.state) === DB_STATE.REMOVED ? '#ffd4d9' : '';

  const columns: TableProps<Tag>['columns'] = [
    {
      key: 'tagStructure',
      title: '',
      dataIndex: 'remove',
      width: 23,
      onCell: (record) => ({
        style: {
          whiteSpace: 'nowrap',
          overflow: 'auto',
          background: getColorRow(record),
          textAlign: 'center',
        },
      }),
      render: (title, node) => (
        <div
          onClick={() => {
            Number(node?.state) === DB_STATE.REMOVED
              ? setRestoreModal(true)
              : setDeleteModal(true);
            setSelectedTag(node);
          }}
          css={style.actionContainer}>
          <IconButton
            icon={
              Number(node?.state) === DB_STATE.REMOVED
                ? ['far', 'rotate-left']
                : ['far', 'circle-xmark']
            }
            customStyle={[
              tabStyles.folderIcon,
              Number(node?.state) === DB_STATE.REMOVED
                ? style.blackIcon
                : style.redIcon,
            ]}
            containerStyle={style.iconRemove}
            backgroundOff
            tooltip={
              Number(node?.state) === DB_STATE.REMOVED
                ? intl.formatMessage({ id: 'restore-tag' })
                : intl.formatMessage({ id: 'delete-tag' })
            }
          />
        </div>
      ),
    },
    {
      key: 'tagStructureTitle',
      title: intl.formatMessage({ id: 'tag' }),
      dataIndex: 'title',
      width: 80,
      onCell: (record) => ({
        style: {
          whiteSpace: 'nowrap',
          overflow: 'auto',
          background: getColorRow(record),
        },
      }),
      render: (title) => <TagAntd color={'#1f5fa6'}>{title}</TagAntd>,
    },
    {
      key: 'tagStructureAction',
      title: intl.formatMessage({ id: 'action' }),
      dataIndex: 'action',
      width: 45,
      align: 'center',
      onCell: (record) => ({
        style: {
          whiteSpace: 'nowrap',
          overflow: 'auto',
          background: getColorRow(record),
          textAlign: 'center',
        },
      }),
      render: (title, record) => (
        <div
          onClick={() => {
            setCreateEditModalVisible(true);
            setSelectedTag(record);
          }}
          css={[style.actionContainer, style.editColor]}>
          <IconButton
            icon={['far', 'pen']}
            customStyle={style.editIcon}
            containerStyle={tabStyles.folderIconContainer}
            backgroundOff
          />
          {intl.formatMessage({ id: 'edit' })}
        </div>
      ),
    },
  ];

  return (
    <div css={style.container}>
      <div css={style.rowContainer}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '10px',
          }}>
          <h3 css={style.heading}>
            {intl.formatMessage({ id: 'tags-settings' })}
          </h3>
          <IconPopup
            customStyle={{ fontSize: '130%' }}
            headerId={'tags-settings-help-header'}
            messageId={'tags-settings-help-body'}
          />
        </div>
        <span css={{ display: 'inline-block', width: 170 }}>
          {intl.formatMessage({ id: 'enable-tags' })}
        </span>

        <Switch
          checked={archiveSettings.data?.resArchiveSettings.is_tags_on}
          disabled={isSwitchLoading}
          onChange={(value) => {
            appLog('value', value);
            setIsSwitchLoading(true);
            doSaveArchiveSettings({
              projectId: projectId!,
              is_tags_on: value,
            });
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div
            css={[style.containerSearch, { marginTop: '10px' }]}
            key="searchBarContainer">
            <Input
              prefix={
                <FontAwesomeIcon
                  icon={['far', 'search']}
                  style={style.icons as CSSProperties}
                />
              }
              css={style.inputField}
              placeholder={intl.formatMessage({ id: 'search' })}
              allowClear
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </div>
          <div
            css={style.buttonDiv}
            onClick={() => setCreateEditModalVisible(true)}>
            <IconButton
              customStyle={style.iconPlus}
              iconStyle="far"
              icon="circle-plus"
              backgroundOff
            />
            <p css={{ marginBottom: 0, marginLeft: 2 }}>
              {intl.formatMessage({ id: 'create-tag' })}
            </p>
          </div>
        </div>
      </div>
      <div css={[style.rowContainer, { marginTop: '20px' }]}>
        <Table<Tag>
          showSorterTooltip={false}
          bordered
          pagination={false}
          scroll={{ y: '45vh', x: 200 }}
          loading={loading}
          columns={columns}
          dataSource={dataSourceWithKeys}
        />
      </div>
      <div
        style={{
          marginTop: '8px',
          display: 'flex',
          justifyContent: 'flex-end',
        }}>
        <Checkbox
          checked={showDeleted}
          onChange={(e) => setShowDeleted(e.target.checked)}>
          {intl.formatMessage({ id: 'show-deleted' })}
        </Checkbox>
      </div>
      {createEditModalVisible && (
        <CreateEditTagModal
          visible={createEditModalVisible}
          tag={selectedTag}
          onCancel={() => {
            setCreateEditModalVisible(false);
            setSelectedTag(null);
          }}
        />
      )}
      <DeleteTagModal
        visible={deleteModal}
        tag={selectedTag}
        onCancel={() => setDeleteModal(false)}
        onDone={() => {
          setDeleteModal(false);
          setSelectedTag(null);
        }}
      />
      <RestoreTagModal
        visible={restoreeModal}
        tag={selectedTag}
        onCancel={() => setRestoreModal(false)}
        onDone={() => {
          setRestoreModal(false);
          setSelectedTag(null);
        }}
      />
    </div>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  container: {},
  iconRemove: {},
  rowContainer: { marginBottom: '8px' },
  tag: {
    marginRight: 3,
    animation: `${fadeInKeyframes} 0.2s ease-out`,
  },
  actionContainer: {
    cursor: 'pointer',
  },
  containerSearch: {
    // display: 'flex',
    // alignItems: 'center',
    // border: `1px solid ${theme.colors.lightGray}`,
    // borderRadius: '2px',
    // margin: '8px 0',
    // paddingLeft: '5px',
    // width: '55%',
    // height: '32px',
    // ':focus': {
    //   border: `1px solid ${theme.colors.primary}`,
    // },
    // // backgroundColor: theme.colors.gray,
  },
  icons: {
    color: theme.colors.text.primary,
    fontSize: '16px',
  },
  inputField: {
    // border: 'none!important',
    // height: '100%',
    // width: '100%',
    // color: `${theme.colors.text.primary}`,
    //
    // '::placeholder': {
    //   color: `${theme.colors.text.secondary}`,
    // },
    //
    // ':focus': {
    //   outline: 'none',
    // },
  },
  buttonDiv: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    type: 'button',
    cursor: 'pointer',
    marginLeft: 4,
    padding: '2px 10px',
    ':hover': {
      backgroundColor: theme.colors.gray,
      borderRadius: 9,
    },
  },
  iconPlus: {
    fontSize: '20px',
    color: theme.colors.primary,
    transition: '0.3s',
  },
  whiteHeader: {
    color: 'white',
  },
  blackIcon: {
    color: theme.colors.darkGray,
  },
  redIcon: {
    color: theme.colors.red,
  },
  editColor: {
    color: theme.colors.darkGray,
    ':hover': {
      color: theme.colors.primary,
    },
  },
  editIcon: {
    fontSize: 18,
    marginTop: -7,
  },
});
